import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { UserBO, UserRepositoryBase, UserRole, UserRoleQuery } from '@skylab-shared/business';
import { EventBusBase, IdentifiableValueConverter, RepositoryResponseCode, SaveForbiddenResponse } from '@skylab-shared/core';

import { UserRepository } from '@skylab-angular-shared/business';
import { CommandBus, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FormContainerComponent, PanelComponent, PanelWrapperComponent, SelectValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { UserModel, UserPermissionKind } from '@skylab-shared/dataModel';
import { DictionaryDialogComponentBase, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface UserDialogResult {
    id: number;
    version: string;
}

type PermissionViewItem = {
    kind: UserPermissionKind,
    label: string;
    description: string;
    selected: boolean;
};

type PermissionGroup = {
    label: string;
    permissions: PermissionViewItem[]
};

interface UserConfigurationViewModel extends DictionaryDialogViewModelBase<UserBO> {
    permissionGroups: PermissionGroup[];
    role: {
        value: number
    };
    roles: UserRole[]
    rolesConverter: IdentifiableValueConverter;
    switchPermission(value: boolean, permission: PermissionViewItem): void;
}

type UpdateUserRoleErrorCode = 'NoMoreAdmins' | 'NoRequiredPermissions';

@Component({
    selector: 'skl-user-dialog',
    templateUrl: './user-dialog.component.html',
    styleUrls: ['./user-dialog.component.scss'],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, SelectValueAccessorDirective, ValidateDirective, FormsModule, PanelWrapperComponent, PanelComponent, BooleanValueAccessorDirective, ButtonComponent, TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UserDialogComponent extends DictionaryDialogComponentBase<UserModel, UserBO, UserRepositoryBase, UserConfigurationViewModel> {
    public constructor(
        @Inject(UserRepository) userRepository: UserRepositoryBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(EventBus) eventBus: EventBusBase,

        toastService: ToastService,
        commandBus: CommandBus,
        dialogService: DialogInsService
    ) {
        super(userRepository, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            super.preloadDataAsync(),
            this.localContext.preload(UserRoleQuery)
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();
        const self = this;

        this.viewModel.roles = this.localContext.getPreloaded(UserRoleQuery) || [];
        this.viewModel.permissionGroups = this.permissionsGroups;
        this.viewModel.rolesConverter = new IdentifiableValueConverter(this.viewModel.roles);
        this.viewModel.role = {
            get value() {
                return self.viewModel.bo.model.roleId;
            },
            set value(val) {
                self.viewModel.bo.model.roleId = val;
                const role = self.localContext.firstPreloaded(UserRoleQuery, {id: val});
                self.viewModel.bo.model.userPermissionKinds = role ? [...role.permissionKinds] : [];
                self.viewModel.permissionGroups = self.permissionsGroups;
            }
        };
        this.viewModel.switchPermission = (value: boolean, permission: PermissionViewItem) => {
            const permissions = self.viewModel.bo.model.userPermissionKinds;
            value
                ? permissions.push(permission.kind)
                : permissions.splice(permissions.findIndex(elem => elem === permission.kind), 1);
        };
    }

    public get permissionsGroups(): PermissionGroup[] {
        const isPermissionSelected = (permission: UserPermissionKind) => this.viewModel.bo.model.userPermissionKinds.includes(permission);

        return [{
            label: this.l10n._T('scenes.configuration.shared.user.dialog.form.permissions.ksef.label'),
            permissions: [
                {
                    kind: UserPermissionKind.SendDocumentsToKsef,
                    label: this.l10n._T('scenes.configuration.shared.user.dialog.form.permissions.ksef.sending.label'),
                    description: this.l10n._T('scenes.configuration.shared.user.dialog.form.permissions.ksef.sending.description'),
                    selected: isPermissionSelected(UserPermissionKind.SendDocumentsToKsef)
                },
                {
                    kind: UserPermissionKind.ReceiveDocumentsFromKsef,
                    label: this.l10n._T('scenes.configuration.shared.user.dialog.form.permissions.ksef.receiving.label'),
                    description: this.l10n._T('scenes.configuration.shared.user.dialog.form.permissions.ksef.receiving.description'),
                    selected: isPermissionSelected(UserPermissionKind.ReceiveDocumentsFromKsef)
                }
            ]
        }];
    }

    protected override onSaveError(response?: SaveForbiddenResponse<UpdateUserRoleErrorCode>): void {
        if (!response || response.code !== RepositoryResponseCode.Forbidden) {
            super.onSaveError(response);
        } else {
            const text = response.kind === 'NoMoreAdmins'
                ? 'scenes.configuration.shared.user.dialog.errors.no-more-admins'
                : 'scenes.configuration.shared.user.dialog.errors.no-required-permission';
            this.toastService.showAsync({type: 'error', text: this.l10n._T(text)});
        }
    }
}
